const HEX_COLOR = /^#?([\da-f]{3}|[\da-f]{6})$/i;

type ColorChannels = [number, number, number];
type OklabColor = [number, number, number];
type OklchColor = [number, number, number];

interface MaterialToneTransform {
  lightnessOffset: number,
  lightnessScale: number,
  chromaScale: number,
}

export type MaterialColorMap = Record<
  '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900'
  | 'A100' | 'A200' | 'A400' | 'A700',
  string
>;

// These transforms are least-squares fits of the hand-tuned Material palettes in OKLCH space.
// Keeping hue constant makes generated shades feel related even for colors outside that data set.
const MATERIAL_TONE_TRANSFORMS: Record<keyof MaterialColorMap, MaterialToneTransform> = {
  '50': {lightnessOffset: 0.8813, lightnessScale: 0.1130, chromaScale: 0.1318},
  '100': {lightnessOffset: 0.7033, lightnessScale: 0.2840, chromaScale: 0.3348},
  '200': {lightnessOffset: 0.4973, lightnessScale: 0.4870, chromaScale: 0.5551},
  '300': {lightnessOffset: 0.2914, lightnessScale: 0.6955, chromaScale: 0.7604},
  '400': {lightnessOffset: 0.1409, lightnessScale: 0.8506, chromaScale: 0.9030},
  '500': {lightnessOffset: 0, lightnessScale: 1, chromaScale: 1},
  '600': {lightnessOffset: -0.0198, lightnessScale: 0.9752, chromaScale: 0.9666},
  '700': {lightnessOffset: -0.0511, lightnessScale: 0.9513, chromaScale: 0.9278},
  '800': {lightnessOffset: -0.0788, lightnessScale: 0.9249, chromaScale: 0.8933},
  '900': {lightnessOffset: -0.1118, lightnessScale: 0.8609, chromaScale: 0.8505},
  A100: {lightnessOffset: 0.4553, lightnessScale: 0.5776, chromaScale: 0.7530},
  A200: {lightnessOffset: 0.2221, lightnessScale: 0.8246, chromaScale: 1.0870},
  A400: {lightnessOffset: 0.1464, lightnessScale: 0.8642, chromaScale: 1.2411},
  A700: {lightnessOffset: 0.0910, lightnessScale: 0.8447, chromaScale: 1.1892},
};

export function normalizeHexColor(color: string): string | null {
  if (!HEX_COLOR.test(color)) {
    return null;
  }

  const value = color.replace('#', '');
  const expanded = value.length === 3
    ? value.split('').map(character => character.repeat(2)).join('')
    : value;

  return `#${expanded.toLowerCase()}`;
}

function colorChannels(color: string): ColorChannels | null {
  const normalized = normalizeHexColor(color);

  if (!normalized) {
    return null;
  }

  return [
    parseInt(normalized.slice(1, 3), 16),
    parseInt(normalized.slice(3, 5), 16),
    parseInt(normalized.slice(5, 7), 16),
  ];
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function channelsToHex(channels: ColorChannels): string {
  return `#${channels
    .map(channel => Math.round(clamp(channel, 0, 255)).toString(16).padStart(2, '0'))
    .join('')}`;
}

function srgbToLinear(channel: number): number {
  const value = channel / 255;

  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(channel: number): number {
  const value = channel <= 0.0031308
    ? 12.92 * channel
    : (1.055 * (Math.max(channel, 0) ** (1 / 2.4))) - 0.055;

  return Math.round(Math.min(1, Math.max(0, value)) * 255);
}

function channelsToOklab([red, green, blue]: ColorChannels): OklabColor {
  const r = srgbToLinear(red);
  const g = srgbToLinear(green);
  const b = srgbToLinear(blue);
  const l = Math.cbrt((0.4122214708 * r) + (0.5363325363 * g) + (0.0514459929 * b));
  const m = Math.cbrt((0.2119034982 * r) + (0.6806995451 * g) + (0.1073969566 * b));
  const s = Math.cbrt((0.0883024619 * r) + (0.2817188376 * g) + (0.6299787005 * b));

  return [
    (0.2104542553 * l) + (0.793617785 * m) - (0.0040720468 * s),
    (1.9779984951 * l) - (2.428592205 * m) + (0.4505937099 * s),
    (0.0259040371 * l) + (0.7827717662 * m) - (0.808675766 * s),
  ];
}

function oklabToLinearSrgb([lightness, a, b]: OklabColor): ColorChannels {
  const l = (lightness + (0.3963377774 * a) + (0.2158037573 * b)) ** 3;
  const m = (lightness - (0.1055613458 * a) - (0.0638541728 * b)) ** 3;
  const s = (lightness - (0.0894841775 * a) - (1.291485548 * b)) ** 3;

  return [
    (4.0767416621 * l) - (3.3077115913 * m) + (0.2309699292 * s),
    (-1.2684380046 * l) + (2.6097574011 * m) - (0.3413193965 * s),
    (-0.0041960863 * l) - (0.7034186147 * m) + (1.707614701 * s),
  ];
}

function oklabToChannels(color: OklabColor): ColorChannels {
  return oklabToLinearSrgb(color).map(linearToSrgb) as ColorChannels;
}

function oklabToOklch([lightness, a, b]: OklabColor): OklchColor {
  return [lightness, Math.hypot(a, b), Math.atan2(b, a)];
}

function oklchToOklab([lightness, chroma, hue]: OklchColor): OklabColor {
  return [lightness, chroma * Math.cos(hue), chroma * Math.sin(hue)];
}

function isOklchInSrgbGamut(color: OklchColor): boolean {
  return oklabToLinearSrgb(oklchToOklab(color))
    .every(channel => channel >= 0 && channel <= 1);
}

function gamutMapOklch([lightness, chroma, hue]: OklchColor): OklchColor {
  const boundedLightness = clamp(lightness, 0, 1);

  if (isOklchInSrgbGamut([boundedLightness, chroma, hue])) {
    return [boundedLightness, chroma, hue];
  }

  let minimumChroma = 0;
  let maximumChroma = chroma;

  for (let iteration = 0; iteration < 16; iteration += 1) {
    const candidateChroma = (minimumChroma + maximumChroma) / 2;

    if (isOklchInSrgbGamut([boundedLightness, candidateChroma, hue])) {
      minimumChroma = candidateChroma;
    } else {
      maximumChroma = candidateChroma;
    }
  }

  return [boundedLightness, minimumChroma, hue];
}

export function mixHexColors(color: string, mixColor: string, colorWeight: number): string | null {
  const channels = colorChannels(color);
  const mixChannels = colorChannels(mixColor);

  if (!channels || !mixChannels || colorWeight < 0 || colorWeight > 1) {
    return null;
  }

  const mixed = channels.map((channel, index) =>
    Math.round((channel * colorWeight) + (mixChannels[index] * (1 - colorWeight)))
  );

  return `#${mixed.map(channel => channel.toString(16).padStart(2, '0')).join('')}`;
}

export function mixHexColorsOklab(color: string, mixColor: string, colorWeight: number): string | null {
  const channels = colorChannels(color);
  const mixChannels = colorChannels(mixColor);

  if (!channels || !mixChannels || colorWeight < 0 || colorWeight > 1) {
    return null;
  }

  const oklab = channelsToOklab(channels);
  const mixOklab = channelsToOklab(mixChannels);
  const mixed = oklab.map((channel, index) =>
    (channel * colorWeight) + (mixOklab[index] * (1 - colorWeight))
  ) as [number, number, number];
  const mixedChannels = oklabToChannels(mixed);

  return `#${mixedChannels.map(channel => channel.toString(16).padStart(2, '0')).join('')}`;
}

export function hexColorWithAlpha(color: string, alpha: number): string | null {
  const channels = colorChannels(color);

  if (!channels || alpha < 0 || alpha > 1) {
    return null;
  }

  return `rgb(${channels.join(' ')} / ${alpha})`;
}

export function buildMaterialColorMap(baseColor: string): MaterialColorMap | null {
  const base = colorChannels(baseColor);

  if (!base) {
    return null;
  }

  const [baseLightness, baseChroma, baseHue] = oklabToOklch(channelsToOklab(base));

  return Object.fromEntries(
    Object.entries(MATERIAL_TONE_TRANSFORMS).map(([tone, transform]) => {
      if (tone === '500') {
        return [tone, channelsToHex(base)];
      }

      const transformedLightness = transform.lightnessOffset + (baseLightness * transform.lightnessScale);
      const numericTone = Number(tone);
      const lightness = numericTone < 500
        ? Math.max(baseLightness, transformedLightness)
        : numericTone > 500
          ? Math.min(baseLightness, transformedLightness)
          : transformedLightness;
      const color = gamutMapOklch([
        lightness,
        baseChroma * transform.chromaScale,
        baseHue,
      ]);

      return [tone, channelsToHex(oklabToChannels(oklchToOklab(color)))];
    }),
  ) as MaterialColorMap;
}

export function perceptualColorDistance(firstColor: string, secondColor: string): number | null {
  const firstChannels = colorChannels(firstColor);
  const secondChannels = colorChannels(secondColor);

  if (!firstChannels || !secondChannels) {
    return null;
  }

  const first = channelsToOklab(firstChannels);
  const second = channelsToOklab(secondChannels);

  return Math.hypot(
    first[0] - second[0],
    first[1] - second[1],
    first[2] - second[2],
  );
}

export function relativeLuminance(color: string): number | null {
  const channels = colorChannels(color);

  if (!channels) {
    return null;
  }

  const [red, green, blue] = channels.map(srgbToLinear);

  return (0.2126 * red) + (0.7152 * green) + (0.0722 * blue);
}

export function contrastRatio(firstColor: string, secondColor: string): number | null {
  const firstLuminance = relativeLuminance(firstColor);
  const secondLuminance = relativeLuminance(secondColor);

  if (firstLuminance === null || secondLuminance === null) {
    return null;
  }

  const lighter = Math.max(firstLuminance, secondLuminance);
  const darker = Math.min(firstLuminance, secondLuminance);

  return (lighter + 0.05) / (darker + 0.05);
}

export function colorContrast(color: string, fallback = '#363636'): string {
  const luminance = relativeLuminance(color);

  if (luminance === null) {
    return fallback;
  }

  const whiteContrast = 1.05 / (luminance + 0.05);
  const darkContrast = (luminance + 0.05) / 0.05;

  return whiteContrast > darkContrast ? '#ffffff' : '#000000';
}

export function isLightColor(color: string): boolean {
  return normalizeHexColor(color) !== null && colorContrast(color) === '#000000';
}

export function isDarkColor(color: string): boolean {
  return normalizeHexColor(color) !== null && colorContrast(color) === '#ffffff';
}
